import { ArrowUp, ArrowDown, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { COLUMN_DEFS } from './inventoryColumns';

const NONE = '__none__';
const DEF_BY_KEY = Object.fromEntries(COLUMN_DEFS.map((d) => [d.key, d]));
const SORTABLE_DEFS = COLUMN_DEFS.filter((d) => d.sortable !== false);

export default function InventoryColumnsDialog({ open, onClose, config, onToggle, onMove, onSort, onReset }) {
  const { t } = useT();
  const cols = config.columns;
  const sortKey = config.sort.key;
  const sortDir = config.sort.dir;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" aria-describedby={undefined} data-testid="columns-dialog">
        <DialogHeader><DialogTitle>{t('mfg.cols.title')}</DialogTitle></DialogHeader>

        <p className="text-xs text-zinc-500">{t('mfg.cols.show')}</p>
        <ul className="divide-y divide-zinc-200 rounded-lg border border-zinc-200">
          {cols.map((c, idx) => {
            const def = DEF_BY_KEY[c.key];
            if (!def) return null;
            return (
              <li key={c.key} className="flex items-center gap-2 px-3 py-2" data-testid={`col-row-${c.key}`}>
                <Checkbox
                  checked={c.visible}
                  disabled={!!def.fixed}
                  onCheckedChange={() => onToggle(c.key)}
                  data-testid={`col-toggle-${c.key}`}
                />
                <span className={`flex-1 text-sm ${c.visible ? 'text-zinc-800' : 'text-zinc-400'}`}>{t(def.labelKey)}</span>
                <button type="button" disabled={idx === 0} onClick={() => onMove(c.key, -1)}
                  className="p-1 rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-30" data-testid={`col-up-${c.key}`}>
                  <ArrowUp size={15} />
                </button>
                <button type="button" disabled={idx === cols.length - 1} onClick={() => onMove(c.key, 1)}
                  className="p-1 rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-30" data-testid={`col-down-${c.key}`}>
                  <ArrowDown size={15} />
                </button>
              </li>
            );
          })}
        </ul>

        <div className="space-y-2">
          <p className="text-xs font-semibold text-zinc-600">{t('mfg.cols.defaultSort')}</p>
          <div className="flex items-center gap-2">
            <Select value={sortKey || NONE} onValueChange={(v) => onSort(v === NONE ? null : v, sortDir)}>
              <SelectTrigger className="h-10 flex-1" data-testid="col-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t('mfg.cols.none')}</SelectItem>
                {SORTABLE_DEFS.map((d) => <SelectItem key={d.key} value={d.key}>{t(d.labelKey)}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex rounded-lg border border-zinc-200 overflow-hidden">
              <button type="button" disabled={!sortKey} onClick={() => onSort(sortKey, 'asc')}
                className={`px-3 h-10 text-sm disabled:opacity-40 ${sortDir === 'asc' && sortKey ? 'bg-orange-500 text-white' : 'bg-white text-zinc-600'}`} data-testid="col-sort-asc">
                {t('mfg.cols.asc')}
              </button>
              <button type="button" disabled={!sortKey} onClick={() => onSort(sortKey, 'desc')}
                className={`px-3 h-10 text-sm border-l border-zinc-200 disabled:opacity-40 ${sortDir === 'desc' && sortKey ? 'bg-orange-500 text-white' : 'bg-white text-zinc-600'}`} data-testid="col-sort-desc">
                {t('mfg.cols.desc')}
              </button>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="outline" onClick={onReset} data-testid="cols-reset-btn" className="text-zinc-600">
            <ArrowCounterClockwise size={15} className="mr-1.5" /> {t('mfg.cols.reset')}
          </Button>
          <Button onClick={onClose} data-testid="cols-done-btn" className="bg-teal-700 hover:bg-teal-800">{t('mfg.cols.done')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
